/* The presentation model for registering a source as the design's four steps.
 *
 * Pure functions, so the two judgements this flow makes — what is wrong with a draft, and what the
 * four checks of a test may honestly claim — are tested without a browser or a server.
 *
 * **The checks are derived, never animated on a timer.** The design draws four rows ticking off one
 * by one. A screen that ticked "Resolve the address" while the request was still in flight would be
 * asserting something nobody had measured yet, so the first three rows run *together* while the
 * verification is in flight — it is one call — and settle together when it answers. The fourth is a
 * separate read of the source's catalogue, so it genuinely runs after them.
 */

import type { ConnectorProfile } from '@/shared/api/generated/catalog';

import type { CredentialField } from './registration';

/** One-based, as the step rail counts. */
export type RegisterStep = 1 | 2 | 3 | 4;

/** What is wrong with one field. Keys for the copy catalogue, never English. */
export type FieldProblem = 'required' | 'portRange' | 'nameTaken' | 'nameTooLong';

export interface EndpointDraft {
  readonly name: string;
  readonly host: string;
  readonly port: string;
  readonly database: string;
}

/** The longest name the server accepts. Checked here so the refusal is beside the field. */
export const MAX_NAME_LENGTH = 200;

/** Every field with a problem, keyed by field name — the four endpoint fields under their own
 * names, the credential fields under the API's.
 *
 * `existingNames` is the tenant's current sources: a duplicate is refused here, beside the field,
 * rather than as a server message under the button. The server stays the authority — this only
 * moves the most common refusal to where the reader is looking.
 *
 * `endpoint: false` checks the credential alone, which is what editing the credential of an
 * already-registered source needs: its name and address are fixed by then.
 */
export function problemsIn({
  draft,
  profile,
  fields,
  values,
  existingNames,
  endpoint = true,
}: {
  readonly draft: EndpointDraft;
  readonly profile: ConnectorProfile;
  readonly fields: readonly CredentialField[];
  readonly values: Readonly<Record<string, string>>;
  readonly existingNames: readonly string[];
  readonly endpoint?: boolean;
}): Readonly<Record<string, FieldProblem>> {
  const problems: Record<string, FieldProblem> = {};
  const blank = (value: string | undefined): boolean => (value ?? '').trim() === '';

  if (endpoint) {
    const name = draft.name.trim();
    if (name === '') problems['name'] = 'required';
    else if (name.length > MAX_NAME_LENGTH) problems['name'] = 'nameTooLong';
    else if (existingNames.some((existing) => existing.trim() === name))
      problems['name'] = 'nameTaken';

    if (profile.needs_endpoint) {
      if (blank(draft.host)) problems['host'] = 'required';
      if (blank(draft.port)) problems['port'] = 'required';
      else if (!isPort(draft.port)) problems['port'] = 'portRange';
    }
    if (blank(draft.database)) problems['database'] = 'required';
  }

  for (const field of fields) {
    if (field.required && blank(values[field.name])) problems[field.name] = 'required';
  }
  return problems;
}

/** A whole number from 1 to 65535, written as digits — `1e3` and `80.0` are not ports. */
export function isPort(text: string): boolean {
  const trimmed = text.trim();
  if (!/^\d{1,5}$/.test(trimmed)) return false;
  const value = Number.parseInt(trimmed, 10);
  return value >= 1 && value <= 65535;
}

/** The four checks of a test, in the order a connection attempt takes them. Their names are the
 * server's `failed_stage` values, so a refusal maps onto a row without translation. */
export const CHECKS = ['resolve', 'connect', 'authenticate', 'catalogue'] as const;
export type Check = (typeof CHECKS)[number];

/** `pending` is a row nothing has reached — not started, or not attempted because an earlier row
 * refused. `unconfirmed` is a row a refusal may have been at: the source said no and did not say
 * where, so neither a tick nor a cross is true. */
export type CheckState = 'pending' | 'running' | 'passed' | 'failed' | 'unconfirmed';

/** What the verification has answered so far. */
export type VerifyReading =
  | { readonly kind: 'running' }
  /** The request itself failed, so nothing was learned about the source. */
  | { readonly kind: 'unasked' }
  | {
      readonly kind: 'answered';
      readonly reachable: boolean;
      readonly failedStage: string | null;
    };

/** The catalogue read, which runs only once the source has been reached. */
export type CatalogueReading = 'idle' | 'running' | 'passed' | 'failed';

export function checkStates(
  verify: VerifyReading,
  catalogue: CatalogueReading,
): Readonly<Record<Check, CheckState>> {
  const all = (state: CheckState): Record<Check, CheckState> => ({
    resolve: state,
    connect: state,
    authenticate: state,
    catalogue: state,
  });

  if (verify.kind === 'running') return { ...all('running'), catalogue: 'pending' };
  if (verify.kind === 'unasked') return all('pending');

  if (verify.reachable) {
    const last: CheckState =
      catalogue === 'idle' ? 'pending' : catalogue === 'running' ? 'running' : catalogue;
    return { ...all('passed'), catalogue: last };
  }

  const at = CHECKS.indexOf(verify.failedStage as Check);
  /* An unrecognised or absent stage: the source refused somewhere in the first three steps (a
   * refusal in the fourth is always named), so those three are unconfirmed and the catalogue read
   * never ran. */
  if (at < 0) return { ...all('unconfirmed'), catalogue: 'pending' };

  const states = all('pending');
  CHECKS.forEach((check, index) => {
    states[check] = index < at ? 'passed' : index === at ? 'failed' : 'pending';
  });
  return states;
}

/** The test's verdict for the step rail and for whether a harvest may be started now. */
export type TestOutcome = 'running' | 'reachable' | 'refused' | 'unasked';

export function testOutcome(
  states: Readonly<Record<Check, CheckState>>,
  unasked: boolean,
): TestOutcome {
  if (unasked) return 'unasked';
  const values = CHECKS.map((check) => states[check]);
  if (values.some((state) => state === 'failed' || state === 'unconfirmed'))
    return 'refused';
  if (values.every((state) => state === 'passed')) return 'reachable';
  return 'running';
}

/** Which step the reader may go back to from the rail: any earlier one, until the source is
 * registered. Nothing is stored before that, so every earlier choice is still the reader's; after
 * it the source exists, and the dialog only finishes what it started. */
export function canReturnTo(
  target: RegisterStep,
  current: RegisterStep,
  registered: boolean,
): boolean {
  return !registered && target < current;
}

/** The two letters on a source's tile. The design's: a source is recognisable before it is read. */
export const TILE_TEXT: Readonly<Record<ConnectorProfile['connector_type'], string>> = {
  postgresql: 'Pg',
  mysql: 'My',
  oracle: 'Or',
  sqlserver: 'Ms',
  snowflake: 'Sf',
  databricks: 'Db',
  bigquery: 'Bq',
};

/** What the register dialog hands the source's own page when it lands there.
 *
 * Router state rather than a query string: it is a one-off message for the arrival, not an address
 * somebody should be able to share. Read through a guard because history state is whatever the last
 * writer put there — another screen, an older build — and a page must not render `undefined is
 * registered`. */
export interface Arrival {
  readonly name: string;
  readonly harvesting: boolean;
}

export function arrivalFrom(state: unknown): Arrival | null {
  if (typeof state !== 'object' || state === null) return null;
  const candidate = state as Record<string, unknown>;
  const name = candidate['registered'];
  const harvesting = candidate['harvesting'];
  if (typeof name !== 'string' || name === '' || typeof harvesting !== 'boolean')
    return null;
  return { name, harvesting };
}
